import type { FlightCardData, FlightPricing } from '@massilia/queries'

import { Button, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@massilia/ui'
import { HelpCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useAgencyPhone } from '@/hooks/use-agency-phone'
import { PASSENGER_TYPE_LABELS } from '@/utils/passenger-labels'

import { useDebug } from './debug-context'
import { FlightConditionsModal } from './flight-conditions-modal'

interface FlightCardPriceProps {
  flight: FlightCardData
  onSelect?: () => void
  pricing: FlightPricing
  selectedJourneyKeys: string[]
  transactionKey: string
}

export function FlightCardPrice({
  flight,
  onSelect,
  pricing,
  selectedJourneyKeys,
  transactionKey,
}: FlightCardPriceProps) {
  const showDebug = useDebug()
  const agencyPhone = useAgencyPhone()
  const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false)
  // Get first passenger price for display - prioritize adult, fallback to first available
  const adultPricing = pricing.perPassenger.find((p) => p.type === 'Adult')
  const displayPricing = adultPricing ?? pricing.perPassenger[0] ?? 0
  const pricePerPerson = parseInt(displayPricing?.pricePerPerson.amount ?? pricing.total.amount)
  const totalePrice = parseInt(pricing.total.amount)
  const passengerTypeLabel = displayPricing
    ? PASSENGER_TYPE_LABELS[displayPricing.type].toLowerCase()
    : 'personne'

  if (flight.callAgencyOnly) {
    return (
      <div className="flex h-full w-full flex-col justify-between space-y-2 bg-gray-50 p-4 md:w-68 md:p-6">
        <div className="flex flex-col items-center gap-4 py-4">
          <p className="text-center text-sm font-medium text-gray-700">
            Pour cette offre, veuillez contacter l&apos;agence
          </p>
          <a
            className="w-full rounded-md bg-linear-to-br from-orange-500 to-[#D84A2D] px-4 py-2 text-center text-sm font-medium text-white hover:opacity-90 md:text-base"
            href={`tel:${agencyPhone.replace(/\s/g, '')}`}
          >
            {agencyPhone}
          </a>
        </div>

        {/* Debug: Account code */}
        {showDebug && pricing.accountCode && (
          <p className="mt-2 w-fit space-y-2 rounded border border-red-200 bg-red-50 p-2 font-mono text-xs text-red-800">
            <span className="font-bold">CODE: {pricing.accountCode}</span>{' '}
          </p>
        )}

        {/* Debug: xRole badges */}
        {showDebug && pricing.xRoles && pricing.xRoles.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {pricing.xRoles.map((role) => (
              <span
                key={role}
                className="w-fit rounded border border-purple-200 bg-purple-50 px-2 py-1 font-mono text-xs font-bold text-purple-800"
              >
                {role}
              </span>
            ))}
          </div>
        )}

        {/* Debug: Margin */}
        {showDebug && pricing.breakdown.margin && (
          <div className="mt-2 space-y-2 rounded border border-yellow-200 bg-yellow-50 p-2 font-mono text-xs text-yellow-800">
            <p className="flex flex-col gap-2 break-all">
              <span>
                <b>
                  {(
                    Number(pricing.breakdown.baseFare.amount) + Number(pricing.breakdown.taxes.amount)
                  ).toFixed(2)}
                </b>
                € Total TTC (base)
              </span>
              <Separator orientation="horizontal" />
              <span>
                <b>{pricing.breakdown.margin.amount}</b>€ Marge totale
              </span>
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col justify-between space-y-2 bg-gray-50 p-4 md:w-68 md:p-6">
      <div className="flex flex-col gap-2">
        {/* Price per person */}
        <div className="mb-1">
          <span className="text-xl font-bold text-blue-600 md:text-2xl">{pricePerPerson} €</span>
          <span className="ml-1 text-xs text-gray-500 md:text-sm">/ par {passengerTypeLabel}</span>
        </div>

        {/* Price details tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button className="mb-2 flex cursor-pointer items-center gap-1 text-xs text-gray-600 hover:text-gray-800 md:text-sm">
              Détail des prix
              <HelpCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            arrowClassName="fill-orange-500 bg-orange-500"
            className="max-w-xs border-none bg-orange-500 p-3 text-white shadow-lg"
          >
            <div className="space-y-1">
              {pricing.perPassenger.map((pp, index) => (
                <div className="flex justify-between gap-4" key={`${pp.type}-${index}`}>
                  <span>
                    {pp.count} x {PASSENGER_TYPE_LABELS[pp.type]}
                  </span>
                  <span>{Number(pp.pricePerPerson.amount).toFixed(0)}€</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between gap-4 border-t border-orange-400 pt-2 font-medium">
                <span>Total</span>
                <span>{Number(pricing.total.amount).toFixed(0)}€</span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>

        {/* Total price */}
        <div className="flex flex-row items-center justify-between">
          <span className="text-base font-medium text-gray-600 md:text-lg">Total</span>
          <div className="text-brand-500 text-lg font-bold md:text-xl">{totalePrice} € TTC</div>
        </div>
      </div>

      {/* Debug: Account code */}
      {showDebug && pricing.accountCode && (
        <p className="mt-2 w-fit space-y-2 rounded border border-red-200 bg-red-50 p-2 font-mono text-xs text-red-800">
          <span className="font-bold">CODE: {pricing.accountCode}</span>{' '}
        </p>
      )}

      {/* Debug: xRole badges */}
      {showDebug && pricing.xRoles && pricing.xRoles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {pricing.xRoles.map((role) => (
            <span
              key={role}
              className="w-fit rounded border border-purple-200 bg-purple-50 px-2 py-1 font-mono text-xs font-bold text-purple-800"
            >
              {role}
            </span>
          ))}
        </div>
      )}

      {/* Debug: Margin with per-passenger breakdown and margin IDs */}
      {showDebug && pricing.breakdown.margin && (
        <div className="mt-2 space-y-2 rounded border border-yellow-200 bg-yellow-50 p-2 font-mono text-xs text-yellow-800">
          <p className="flex flex-col gap-2 break-all">
            <span>
              <b>
                {(
                  Number(pricing.breakdown.baseFare.amount) + Number(pricing.breakdown.taxes.amount)
                ).toFixed(2)}
              </b>
              € Total TTC (base)
            </span>
            <Separator orientation="horizontal" />
            {/* Per-passenger margin breakdown with margin IDs */}
            {pricing.breakdown.margin.perPassenger.length > 0 && (
              <span className="flex flex-col gap-1">
                <b>Marge par passager:</b>
                {pricing.breakdown.margin.perPassenger.map((pm, idx) => (
                  <span className="ml-2 flex flex-col" key={idx}>
                    <span>
                      {pm.count} x {pm.type}: <b>{pm.marginPerPerson}€</b>/pax ={' '}
                      {(pm.count * parseFloat(pm.marginPerPerson)).toFixed(2)}€
                    </span>
                    {/* Détail des marges combinées (allotement + normale) */}
                    {pm.allotmentMarginId || pm.normalMarginId ? (
                      <span className="ml-2 flex flex-col gap-0.5">
                        {pm.allotmentMarginId && (
                          <span className="flex flex-col text-purple-700">
                            <span>
                              ↳ Marge allotement: <b>{pm.allotmentMarginPerPerson}€</b>/pax
                              {pm.allotmentOutboundAmount && pm.allotmentReturnAmount && (
                                <span className="ml-1 text-purple-500">
                                  (aller: {pm.allotmentOutboundAmount}€ / retour: {pm.allotmentReturnAmount}€)
                                </span>
                              )}
                            </span>
                            <button
                              className="cursor-pointer text-left text-xs text-purple-500 hover:underline"
                              onClick={() => {
                                void navigator.clipboard.writeText(pm.allotmentMarginId!).then(() => {
                                  toast.info('Identifiant de marge allotement copié')
                                })
                              }}
                              title="Cliquez pour copier"
                            >
                              {pm.allotmentMarginId}
                            </button>
                          </span>
                        )}
                        {pm.normalMarginId && (
                          <span className="flex flex-col text-blue-700">
                            <span>
                              ↳ Marge normale: <b>{pm.normalMarginPerPerson}€</b>/pax
                              {pm.normalMarginMultiplier != null &&
                                pm.normalMarginMultiplier !== 1 && (
                                  <span className="ml-1 text-blue-500">
                                    ({pm.normalMarginPerPersonRaw}€ × {pm.normalMarginMultiplier} —
                                    allotement{' '}
                                    {pm.allotmentOutboundAmount != null && pm.allotmentReturnAmount == null
                                      ? 'aller'
                                      : pm.allotmentReturnAmount != null && pm.allotmentOutboundAmount == null
                                        ? 'retour'
                                        : 'un sens'}{' '}
                                    seul)
                                  </span>
                                )}
                            </span>
                            <button
                              className="cursor-pointer text-left text-xs text-blue-500 hover:underline"
                              onClick={() => {
                                void navigator.clipboard.writeText(pm.normalMarginId!).then(() => {
                                  toast.info('Identifiant de marge normale copié')
                                })
                              }}
                              title="Cliquez pour copier"
                            >
                              {pm.normalMarginId}
                            </button>
                          </span>
                        )}
                      </span>
                    ) : pm.marginId ? (
                      <button
                        className="cursor-pointer text-left text-xs text-yellow-600 hover:underline"
                        onClick={() => {
                          if (!pm.marginId) return
                          void navigator.clipboard.writeText(pm.marginId).then(() => {
                            toast.info('Identifiant de marge copié dans le presse-papier')
                          })
                        }}
                        title="Cliquez pour copier dans le presse-papier"
                      >
                        {pm.marginId}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Aucune marge</span>
                    )}
                  </span>
                ))}
              </span>
            )}
            <Separator orientation="horizontal" />
            <span>
              <b>{pricing.breakdown.margin.amount}</b>€ Marge totale
            </span>
          </p>
        </div>
      )}

      <div>
        <button
          className="mb-4 text-xs text-gray-600 underline hover:scale-101 hover:cursor-pointer md:text-sm"
          onClick={() => {
            setIsConditionsModalOpen(true)
          }}
        >
          Voir les conditions du billet
        </button>
        {/* Select button */}
        <Button
          className="w-full bg-linear-to-br from-orange-500 to-[#D84A2D] text-sm text-white hover:cursor-pointer hover:opacity-90 md:text-base"
          onClick={() => {
            onSelect?.()
          }}
        >
          Sélectionner ce vol
        </Button>
      </div>

      <FlightConditionsModal
        flight={flight}
        isOpen={isConditionsModalOpen}
        onClose={() => {
          setIsConditionsModalOpen(false)
        }}
        onSelectFlight={onSelect}
        selectedJourneyKeys={selectedJourneyKeys}
        transactionKey={transactionKey}
      />
    </div>
  )
}
